import { LayoutObjectDto } from '@yard/app/api/model/layout-object-dto'
import { LayoutObjectType } from '@yard/app/api/model/layout-object-type'
import { RouteStopResponse } from '@yard/app/api/model/route-stop-response'
import { YardBlockDto } from '@yard/app/api/model/yard-block-dto'
import { YardBlockType } from '@yard/app/api/model/yard-block-type'
import { BAY_WIDTH, ROW_HEIGHT } from '@yard/pages/YardSetup/helpers/yard-view.utils'

// Positions in `Stop.position` are world coordinates — unrotated, unscaled.
// The component applies viewport rotation/scale via fx()/fy().
export interface Stop {
  kind: 'block' | 'handover'
  id: string
  name: string
  position: { x: number; y: number }
  nodeId: string
}

const FLEX_BLOCK_BAYS = 3
const FLEX_BLOCK_ROWS = 3

export const getBlockCenter = (block: YardBlockDto): { x: number; y: number } => {
  const bays = block.type === YardBlockType.FlexibleBlock ? FLEX_BLOCK_BAYS : block.maxBays
  const rows = block.type === YardBlockType.FlexibleBlock ? FLEX_BLOCK_ROWS : block.maxRows
  return {
    x: block.layoutPosition.x + (bays * BAY_WIDTH) / 2,
    y: block.layoutPosition.y + (rows * ROW_HEIGHT) / 2,
  }
}

export const getHandoverCenter = (lo: LayoutObjectDto): { x: number; y: number } => ({
  x: lo.layoutPosition.x + lo.width / 2,
  y: lo.layoutPosition.y + lo.height / 2,
})

export const resolveStop = (
  stop: RouteStopResponse,
  yardBlocks: Record<string, YardBlockDto>,
  allYardBlocks: YardBlockDto[],
  layoutObjectsById: Record<string, LayoutObjectDto>,
): Stop | null => {
  if (stop.handoverAreaId) {
    const handover = layoutObjectsById[stop.handoverAreaId]
    if (!handover || handover.type !== LayoutObjectType.HandoverArea) return null
    return {
      kind: 'handover',
      id: handover.id,
      name: handover.name,
      position: getHandoverCenter(handover),
      nodeId: stop.nodeId,
    }
  }
  if (stop.blockId) {
    const block = yardBlocks[stop.blockId]
    if (!block) return null
    return {
      kind: 'block',
      id: block.id,
      name: block.name,
      position: getBlockCenter(block),
      nodeId: stop.nodeId,
    }
  }
  if (stop.accessPointId) {
    const block = allYardBlocks.find(b =>
      b.accessPoints.some(ap => ap.id === stop.accessPointId),
    )
    if (!block) return null
    return {
      kind: 'block',
      id: block.id,
      name: block.name,
      position: getBlockCenter(block),
      nodeId: stop.nodeId,
    }
  }
  return null
}

/**
 * Resolves route stops to mini-map stops, applies consecutive-duplicate dedup,
 * and appends unreachable blocks. Pure function — all store data passed in.
 */
export const buildStopList = (
  routeStops: RouteStopResponse[],
  unreachableBlockIds: string[],
  yardBlocks: Record<string, YardBlockDto>,
  allYardBlocks: YardBlockDto[],
  layoutObjectsById: Record<string, LayoutObjectDto>,
): Stop[] => {
  const resolvedStops: Stop[] = []
  for (const s of routeStops) {
    if (s.kind !== 'Inbound' && s.kind !== 'Outbound') continue
    const resolved = resolveStop(s, yardBlocks, allYardBlocks, layoutObjectsById)
    if (resolved) resolvedStops.push(resolved)
  }

  // Consecutive-duplicate dedup by id: A→A→B collapses, A→B→A stays.
  const stops: Stop[] = []
  for (const s of resolvedStops) {
    if (stops.at(-1)?.id !== s.id) stops.push(s)
  }

  // Unreachable blocks are still delivery targets — list them so the driver
  // sees all planned stops even when the road network is incomplete. Empty
  // nodeId marks them as not participating in the turnaround split.
  const stopIdSet = new Set(stops.map(s => s.id))
  for (const id of unreachableBlockIds) {
    if (stopIdSet.has(id)) continue
    const block = yardBlocks[id]
    if (!block) continue
    stops.push({
      kind: 'block',
      id: block.id,
      name: block.name,
      position: getBlockCenter(block),
      nodeId: '',
    })
    stopIdSet.add(block.id)
  }

  return stops
}
